// Motion.jsx — Phase 18 redesign.
//
// Thin wrappers around framer-motion so individual pages can add
// scroll-triggered animations without importing motion directly.
//
// <FadeUp>...</FadeUp>          — fades in + slides up when scrolled into view
// <StaggerList delay={0.08}>     — stagger-animate immediate children
//   <FadeUp>card 1</FadeUp>
//   <FadeUp>card 2</FadeUp>
// </StaggerList>
//
// Designed to be drop-in: take a children-only API, default props that
// look good without any tuning. Honours prefers-reduced-motion.

import { motion } from "framer-motion";

const VIEWPORT = { once: true, amount: 0.2 };

export function FadeUp({ children, delay = 0, y = 16, duration = 0.5, className, ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEWPORT}
      transition={{ duration, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function FadeIn({ children, delay = 0, duration = 0.4, className, ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={VIEWPORT}
      transition={{ duration, delay }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function StaggerList({ children, delay = 0.08, className, ...rest }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
      variants={{
        hidden: {},
        show: {
          transition: { staggerChildren: delay, delayChildren: 0.05 },
        },
      }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, y = 20, className, ...rest }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y },
        show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] } },
      }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * Hover-lift card — adds the same lift+glow effect we have in CSS but
 * via framer-motion so it can be combined with whileInView entry
 * animations cleanly.
 */
export function HoverLift({ children, lift = 4, className, ...rest }) {
  return (
    <motion.div
      whileHover={{ y: -lift, transition: { duration: 0.18 } }}
      whileTap={{ y: -lift / 2, transition: { duration: 0.08 } }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
